import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import Icon from './Icon'

const LOADING_TIPS = [
  '★ 活下来 > 一切：永远保留生存筹码',
  '★ 见信号再出手，绝不凭感觉盲目交易',
  '★ 双锁齐开：趋势 confirmations + 买卖信号确认',
  '★ 止损纪律是生死线，跌破立刻果断执行',
  '★ 仓位控制是散户第一护身符',
  '★ 宁可错过，绝不做无把握的交易',
  '★ 跌破白线防守位，纪律高于幻想',
  '★ 确定性第一：只在右侧信号成立时开仓',
]

interface PageLoaderProps {
  /** 手动控制或扩展传入的加载标志 */
  isLoading?: boolean
}

/**
 * 全局像素风格 PageLoader 组件
 * 在页面切换或数据加载时展示像素风格的 Trading/KLine 动画与随机 Z 哥交易金句
 */
export default function PageLoader({ isLoading: externalLoading }: PageLoaderProps) {
  const location = useLocation()
  const [internalLoading, setInternalLoading] = useState(false)
  const [tipIndex, setTipIndex] = useState(0)

  // 监听路由 path / search 变化，触发页面切换时的像素加载状态
  useEffect(() => {
    setInternalLoading(true)
    setTipIndex(Math.floor(Math.random() * LOADING_TIPS.length))

    // 页面切换短展示（360ms），既有丝滑像素反馈又不拖沓
    const timer = setTimeout(() => {
      setInternalLoading(false)
    }, 360)

    return () => clearTimeout(timer)
  }, [location.pathname, location.search])

  const showLoading = externalLoading || internalLoading

  if (!showLoading) return null

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-[color:var(--color-bg-deep)]/80 backdrop-blur-[2px] transition-opacity duration-200"
      style={{ imageRendering: 'pixelated' }}
    >
      <div className="pixel-box pixel-box-bright p-5 md:p-6 max-w-xs sm:max-w-sm w-11/12 text-center shadow-2xl relative bg-[color:var(--color-bg-base)] border-4 border-[color:var(--color-border-bright)] animate-in fade-in zoom-in-95 duration-150">
        {/* 顶部像素 Header */}
        <div className="flex items-center justify-center gap-2 mb-3">
          <Icon name="sparkle" size={14} className="text-[color:var(--color-gold)]" />
          <span className="font-pixel text-xs text-[color:var(--color-text-bright)] tracking-widest">
            SYSTEM LOADING
          </span>
          <Icon name="sparkle" size={14} className="text-[color:var(--color-gold)]" />
        </div>

        {/* 动态像素 K 线动画 */}
        <div className="flex items-end justify-center gap-2.5 h-14 my-3 py-2 border-b-2 border-t-2 border-[color:var(--color-border)] relative overflow-hidden bg-[color:var(--color-bg-elevated)]/60">
          <div className="flex flex-col items-center animate-pixel-bounce-1">
            <div className="w-[2px] h-2 bg-[color:var(--color-bull)] opacity-80" />
            <div className="w-3.5 h-6 bg-[color:var(--color-bull)] border border-pink-500 shadow-sm" />
            <div className="w-[2px] h-2 bg-[color:var(--color-bull)] opacity-80" />
          </div>
          <div className="flex flex-col items-center animate-pixel-bounce-2">
            <div className="w-[2px] h-2.5 bg-[color:var(--color-bear)] opacity-80" />
            <div className="w-3.5 h-4 bg-[color:var(--color-bear)] border border-emerald-500 shadow-sm" />
            <div className="w-[2px] h-2 bg-[color:var(--color-bear)] opacity-80" />
          </div>
          <div className="flex flex-col items-center animate-pixel-bounce-3">
            <div className="w-[2px] h-3 bg-[color:var(--color-bull)] opacity-80" />
            <div className="w-3.5 h-8 bg-[color:var(--color-bull)] border border-pink-500 shadow-sm" />
            <div className="w-[2px] h-1.5 bg-[color:var(--color-bull)] opacity-80" />
          </div>
          <div className="flex flex-col items-center animate-pixel-bounce-4">
            <div className="w-[2px] h-1.5 bg-[color:var(--color-gold)] opacity-80" />
            <div className="w-3.5 h-5 bg-[color:var(--color-gold)] border border-amber-500 shadow-sm" />
            <div className="w-[2px] h-2.5 bg-[color:var(--color-gold)] opacity-80" />
          </div>
        </div>

        {/* 像素进度条 */}
        <div className="w-full bg-[color:var(--color-bg-deep)] h-2.5 border-2 border-[color:var(--color-border-bright)] relative overflow-hidden mb-3">
          <div className="absolute top-0 bottom-0 w-2/5 bg-gradient-to-r from-[color:var(--color-bull)] via-[color:var(--color-gold)] to-[color:var(--color-bear)] animate-pixel-scan" />
        </div>

        {/* 随机 Z 哥语录提示 */}
        <div className="font-pixel-body text-xs text-[color:var(--color-text-dim)] leading-snug min-h-[1.5rem] flex items-center justify-center">
          {LOADING_TIPS[tipIndex]}
        </div>
      </div>
    </div>
  )
}
